import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, PlayCircle, ArrowLeft, Trophy, Brain, FileText } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function CoursePlayer() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [quizAnswer, setQuizAnswer] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/courses/${slug}`);
      setCourse(data);
      setActiveLesson(data.lessons[0]);
      try {
        const enrs = await api.get("/enrollments/me");
        let mine = enrs.data.find(e => e.course_id === data.course_id);
        if (!mine) {
          const created = await api.post("/enrollments", { course_id: data.course_id });
          mine = created.data;
        }
        setEnrollment(mine);
      } catch {}
    })();
  }, [slug]);

  if (!course) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" /></div>;

  const completed = new Set(enrollment?.completed_lessons || []);
  const isDone = (id) => completed.has(id);

  const markComplete = async () => {
    if (!activeLesson) return;
    try {
      const { data } = await api.post("/progress", { course_id: course.course_id, lesson_id: activeLesson.lesson_id, completed: true });
      setEnrollment((e) => ({ ...e, completed_lessons: data.completed_lessons, progress: data.progress }));
      toast.success("Lesson completed! +10 XP");
    } catch {
      toast.error("Sign in to save your progress.");
    }
  };

  return (
    <main className="pt-24 bg-[#F8F9FA] min-h-screen pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm font-poppins text-slate-600 hover:text-[#FF6B35] mb-4" data-testid="player-back">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <section className="lg:col-span-8">
            <div className="ali-card overflow-hidden">
              <div className="aspect-video bg-[#0A192F]">
                {activeLesson?.video_url ? (
                  <iframe
                    key={activeLesson.lesson_id}
                    src={activeLesson.video_url}
                    title={activeLesson.title}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : null}
              </div>
              <div className="p-6">
                <p className="text-xs uppercase tracking-widest text-[#FF6B35] font-poppins font-semibold">{course.category}</p>
                <h1 className="mt-1 font-satoshi font-black text-2xl text-[#0A192F]">{activeLesson?.title}</h1>
                <p className="mt-2 text-slate-600 font-inter">{activeLesson?.description}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <button onClick={markComplete} className="ali-btn-primary" data-testid="mark-complete">
                    {activeLesson && isDone(activeLesson.lesson_id) ? "Completed ✓" : "Mark complete"}
                  </button>
                  <button className="ali-btn-outline" data-testid="download-resources"><FileText className="inline w-4 h-4 mr-2" />Resources</button>
                </div>
              </div>
            </div>

            {/* Quiz */}
            {course.quizzes && course.quizzes.length > 0 && (
              <div className="ali-card p-6 mt-6">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#FF6B35]" />
                  <h2 className="font-satoshi font-bold text-[#0A192F]">Quick Quiz</h2>
                </div>
                <p className="mt-2 font-inter text-slate-700">{course.quizzes[0].question}</p>
                <div className="mt-4 space-y-2">
                  {course.quizzes[0].options.map((o, i) => (
                    <button
                      key={i}
                      onClick={() => setQuizAnswer(i)}
                      className={`w-full text-left px-4 py-3 rounded-xl border font-inter transition ${
                        quizAnswer === i
                          ? i === course.quizzes[0].correct_index
                            ? "border-emerald-400 bg-emerald-50 text-emerald-700"
                            : "border-rose-400 bg-rose-50 text-rose-700"
                          : "border-slate-200 hover:border-[#FF6B35]"
                      }`}
                      data-testid={`quiz-option-${i}`}
                    >
                      {o}
                    </button>
                  ))}
                </div>
                {quizAnswer !== null && (
                  <p className={`mt-3 text-sm font-poppins ${quizAnswer === course.quizzes[0].correct_index ? "text-emerald-600" : "text-rose-600"}`}>
                    {quizAnswer === course.quizzes[0].correct_index ? "Correct! +5 XP" : "Not quite — try again."}
                  </p>
                )}
              </div>
            )}
          </section>

          {/* Lesson list */}
          <aside className="lg:col-span-4">
            <div className="ali-card p-5 sticky top-28">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-satoshi font-bold text-[#0A192F]">{course.title}</h3>
                <span className="text-xs font-poppins text-slate-500">{enrollment?.progress || 0}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
                <div className="h-full bg-gradient-to-r from-[#FF6B35] to-[#FF9061]" style={{ width: `${enrollment?.progress || 0}%` }} />
              </div>
              <ul className="space-y-2 max-h-[60vh] overflow-y-auto">
                {course.lessons.map((l, i) => (
                  <li key={l.lesson_id}>
                    <button
                      onClick={() => setActiveLesson(l)}
                      className={`w-full text-left flex items-center gap-3 p-3 rounded-xl transition ${
                        activeLesson?.lesson_id === l.lesson_id ? "bg-orange-50" : "hover:bg-slate-50"
                      }`}
                      data-testid={`lesson-btn-${l.lesson_id}`}
                    >
                      {isDone(l.lesson_id)
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                        : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                      <div className="flex-1">
                        <p className="text-sm font-poppins font-medium text-[#0A192F]">{i + 1}. {l.title}</p>
                        <p className="text-xs text-slate-500">{l.duration}</p>
                      </div>
                      <PlayCircle className="w-4 h-4 text-[#FF6B35]" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
